import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'
import classNames from 'classnames'

// components
import { MultiSelect } from 'primereact/multiselect'
import MultiSelectHeader from 'primereact-raytm-theme/MultiSelectHeader'
import MultiSelectFooter from 'primereact-raytm-theme/MultiSelectFooter'

// styles
import style from '@aytm/app/stats/page/question-views/bpm/components/mds/styles/Controls.module.scss'

import {
  prepareOptionsChartLegends,
  prepareOptionsForControls,
  getPlaceholder
} from '@aytm/app/stats/page/question-views/bpm/lib/utils'

import { calcColors, getStatistics } from '@aytm/app/stats/page/question-views/bpm/components/mds/lib/utils'
import { NO_PRINT } from '@aytm/app/stats/page/question-views/components/lib/constants'

// Percentile legend + projection stats, mounted by Block in the header row next to
// the mode tabs. Color legend stays gated to a single selected entity — it explains
// the point coloring that only appears in that state.
export const HeaderStats = ({ answer, colorGradients, mdsBrandsValue }) => {
  if ('loading' === answer) {
    return null
  }

  const stats = getStatistics(answer)
  const colors = calcColors(colorGradients)
  const showColors = mdsBrandsValue.length === 1

  return (
    <div className={style.headerStats}>
      <div
        className={classNames(style.colors, {
          [style.colorsShow]: showColors
        })}
      >
        <div className={style.colorsGrid}>
          {colors.map(({ text, color }, index) => (
            <div key={index} className={style.colorItem}>
              <span className={style.colorText}>{text}</span>
              <span className={style.colorValue} style={{ backgroundColor: color }} />
            </div>
          ))}
        </div>
        <div className={style.colorNote}>Relative score for a given attribute in comparison to other entities</div>
      </div>
      <div className={style.statsText}>
        {stats.showRsq && (
          <div>
            2D projection accuracy (R<sup>2</sup>) = <b>{stats.rsqPercentage}%</b>
          </div>
        )}
        <div>
          Tested Entities&apos; scores range: <b>{stats.scoreRange}</b>
        </div>
      </div>
    </div>
  )
}

HeaderStats.displayName = 'BpmMdsHeaderStats'

HeaderStats.propTypes = {
  answer: answerPropType,
  colorGradients: PropTypes.shape({
    percents: PropTypes.array,
    colors: PropTypes.array
  }),
  mdsBrandsValue: PropTypes.arrayOf(PropTypes.string).isRequired
}

const Controls = ({
  answer,
  mdsBrandsValue,
  mdsAttributesValue,
  questionVariants,
  chartLegendMode,
  batches,
  onModelChange
}) => {
  // Early return while Backbone model is loading data
  if (answer === 'loading') {
    return null
  }

  const entitiesOptions = prepareOptionsChartLegends(questionVariants, chartLegendMode)
  const attributesOptions = prepareOptionsForControls(batches)

  // the value comes off the event itself: the list's own Deselect all sends it without a field name
  const onFilterChange = name => event => onModelChange({ [name]: event.value })

  return (
    <div className={`${style.row} ${NO_PRINT}`}>
      <MultiSelect
        data-testid="stat-bpm-mds-entities"
        name="mdsBrandsValue"
        className={classNames(style.select, 'p-multiselect-rounded')}
        placeholder={`Filter by entities: ${getPlaceholder(mdsBrandsValue, entitiesOptions)}`}
        fixedPlaceholder
        value={mdsBrandsValue}
        options={entitiesOptions}
        optionValue="value"
        optionLabel="text"
        panelHeaderTemplate={MultiSelectHeader}
        panelFooterTemplate={MultiSelectFooter}
        removeIcon={null}
        onChange={onFilterChange('mdsBrandsValue')}
      />
      <MultiSelect
        data-testid="stat-bpm-mds-attributes"
        name="mdsAttributesValue"
        className={classNames(style.select, 'p-multiselect-rounded')}
        placeholder={`Filter by attributes: ${getPlaceholder(mdsAttributesValue, attributesOptions)}`}
        fixedPlaceholder
        value={mdsAttributesValue}
        options={attributesOptions}
        optionValue="value"
        optionLabel="text"
        panelHeaderTemplate={MultiSelectHeader}
        panelFooterTemplate={MultiSelectFooter}
        removeIcon={null}
        onChange={onFilterChange('mdsAttributesValue')}
      />
    </div>
  )
}

Controls.displayName = 'BpmControlsMds'

Controls.propTypes = {
  answer: answerPropType,
  chartLegendMode: PropTypes.string.isRequired,
  questionVariants: PropTypes.arrayOf(PropTypes.object).isRequired,
  batches: PropTypes.arrayOf(PropTypes.object).isRequired,
  mdsBrandsValue: PropTypes.arrayOf(PropTypes.string).isRequired,
  mdsAttributesValue: PropTypes.arrayOf(PropTypes.string).isRequired,
  onModelChange: PropTypes.func.isRequired
}

export default Controls
